import { apiBlog } from "@/api/blog.api.ts";
import i18n from "@/i18n.ts";
import { BlogAttachmentPreviewModal } from "@/pages/blog/components/BlogAttachmentPreviewModal.tsx";
import { isBlogAttachmentPreviewable } from "@/types/blog.types.ts";
import { ApiFile } from "@/types/common.types.ts";
import { useModals, useToasts } from "@skailer-com/uikit";
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

const EMPTY_FILES: ApiFile[] = [];

export function useBlogAttachmentDeepLink(postId?: number, files: ApiFile[] = EMPTY_FILES) {
  const [searchParams] = useSearchParams();
  const { openModal } = useModals();
  const { showError } = useToasts();
  const attachmentUuid = searchParams.get("attachment");
  const processedAttachmentRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!postId || !attachmentUuid) return;

    const requestKey = `${postId}:${attachmentUuid}`;
    if (processedAttachmentRef.current === requestKey) return;
    processedAttachmentRef.current = requestKey;

    const showAttachmentNotFound = () => {
      showError({ title: i18n.t("api_errors:blog_attachment_not_found") });
    };

    const file = files.find(({ uuid }) => uuid === attachmentUuid);
    if (!file) {
      showAttachmentNotFound();
      return;
    }

    if (isBlogAttachmentPreviewable(file)) {
      openModal(BlogAttachmentPreviewModal, { postId, file });
      return;
    }

    void (async () => {
      const { data } = await apiBlog.getAttachmentLink(postId, file.uuid);
      if (data?.download_url) {
        window.open(data.download_url, "_self", "noopener,noreferrer");
      } else {
        showAttachmentNotFound();
      }
    })();
  }, [attachmentUuid, files, openModal, postId, showError]);
}
